<script lang="ts" setup generic="T extends BaseObj">
import type DetailViewExpose from '@/objects/DetailViewExpose';
import type { BaseObj } from '@/objects/BaseObj';
import { IconComp, ButtonComp, ModalComp, LoadingComp, AlertMessage, ButtonGroup } from '@/components';
import { ref, watch, type Component, type ComponentInstance, onMounted, toValue, computed } from 'vue';
import { $ti, $tm } from '@/.';
import { useKeyboardListener } from '@/keyboard/keyboard';
import { useDialog, type TaskLoaderState } from '@/composeables';
import type { IDetailModalExposed } from '../Detail/IDetailModalExposed';
import type { ModalSize } from '../Modal/ModalProps';
import { GetHighestModalZindex, randomstring } from '@/utilities/utils';
import { type DropdownItemButton, FieldAlertMessage } from '@/objects';

/**
 * Set up the props for the component
 */
const props = withDefaults(
    defineProps<{
        /**
         * The id of the object to display in the detail view
         * If this is null, the modal will not be shown
         */
        id: number | null;
        /**
         * The detail view component to use
         * This component should have a prop 'id' that is the id of the object to display
         */
        detailView: Component<{ id: number }>;

        /**
         * The size of the detail modal
         * default 'lg'
         */
        detailSize?: ModalSize;

        /**
         * Additional props to pass to the detail view component
         */
        detailViewParams?: any;

        /**
         * Type helper function to transform the data before passing it to the detail view
         * @param a
         */
        THelper: (a: T) => T;

        /**
         * The state of the save button
         */
        saveState?: TaskLoaderState;

        /**
         * The loader to save the state
         */
        saveStateLoader?: (loader: () => Promise<void>, key: string, icon: string | null, singleloader?: boolean) => Promise<void>;

        /**
         * If the modal should stay open after saving
         * @default false
         */
        keepOpenOnSave?: boolean;

        /**
         * Actions to show in the footer before the close and save buttons
         */
        footerActions?: Array<DropdownItemButton<(id: number) => any | Promise<any>>>;
    }>(),
    {
        keepOpenOnSave: false,
    },
);

const { addKeyboardShortcut, removeKeyboardShortcut } = useKeyboardListener();

const modalUniqueId = randomstring('modal-');
const isVisible = (visible: boolean) => {
    // set up keyboard shortcuts
    if (visible) {
        addKeyboardShortcut({
            alias: modalUniqueId,
            on: 'CTRL+S',
            action: saveDetail,
        });
        addKeyboardShortcut({
            alias: modalUniqueId,
            on: 'Escape',
            action: () => tryCloseDetail(),
        });
    } else {
        removeKeyboardShortcut(modalUniqueId);
    }
};

// Toggle the modal
const showDetail = (id: number | null) => {
    if (id !== null) {
        show();
    } else {
        hide();
    }
};

/**
 * Show the modal
 */
const show = () => modaldialog.value?.show();

/**
 * Hide the modal
 */
const hide = async () => await modaldialog.value?.hide();

// Watch the id and show the modal if it is set
watch(() => props.id, showDetail);

// Emit the saveDetail event
const emit = defineEmits<{
    (e: 'saveDetail', value: T, refreshOnlyIds?: number[]): void;
    (e: 'closed'): void;

    // We emit this when the det
    (e: 'detailLoaded', value: T): void;
}>();

// bubble the closed event
const modalClosed = () => emit('closed');

// Set up the refs for the component
const modaldialog = ref<ComponentInstance<typeof ModalComp>>();
const detailComp = ref<DetailViewExpose<T> | null>(null);

/**
 * If we can save the detail. This is true if the detail component has changes and there are no error messages
 * Warning messages will be ignored
 */
const canSave = computed(() => {
    if (detailComp?.value?.disableSave === true) {
        return false;
    }
    return detailComp?.value?.changed && (detailcompErrorMessages.value.length == 0 || externalErrorMessage.value != null);
});

/**
 * This is the list of all the error messages from the detail component
 * These will block the save button
 */
const detailcompErrorMessages = computed(() => {
    if (externalErrorMessage.value != null) {
        return [externalErrorMessage.value];
    }
    return (detailComp?.value?.messages ?? []).filter((x) => x.alertMessage?.type == 'danger');
});

/**
 * This is the list of all the warning messages from the detail component
 * These will not block the save button
 */
const detailcompWarningMessages = computed(() => {
    return (detailComp?.value?.messages ?? []).filter((x) => x.alertMessage?.type == 'warning');
});

/**
 * This is error that is triggered from the parent component
 */
const externalErrorMessage = ref<FieldAlertMessage | null>(null);

// Save the detail
const saveDetail = async () => {
    if (!canSave.value) {
        return;
    }

    // We invoke the save function on the detail component if it has one
    if (detailComp.value != null && detailComp.value.save != null) {
        let ids = await detailComp.value.save(props.saveStateLoader, props.saveState);
        if (ids.length > 0) {
            detailComp.value.changed = false;

            if (!props.keepOpenOnSave) {
                tryCloseDetail();
            }

            emit('saveDetail', detailComp.value.val!, ids);
        }
    } else {
        emit('saveDetail', detailComp.value!.val!);
    }
};

/**
 * Check if the detail has changed and ask for confirmation if it has.
 * @param e
 */
const tryCloseDetail = async () => {
    // If the modal is open and is the highest modal, we can close it or else we just ignore the close
    if (!modaldialog.value || modaldialog.value.zIndex <= GetHighestModalZindex()) {
        return;
    }

    const hideSelfAndEmitClosed = async () => {
        await hide();
        emit('closed');
    };

    if (detailComp.value?.changed) {
        const dialog = await useDialog().ConfirmClose({ yesCallback: () => true });
        const confirmClose = await dialog.closePromise;
        if (confirmClose) {
            await hideSelfAndEmitClosed();
        }
    } else {
        await hideSelfAndEmitClosed();
    }
};

const getTitle = () => {
    let title = toValue(detailComp.value?.title);
    if (title?.includes('undefined')) {
        title = '';
    }
    return title;
};

// Show the modal if a id is set
onMounted(() => showDetail(props.id));

watch(
    () => detailComp.value?.val,
    (nv, ov) => {
        if (nv && !ov) {
            emit('detailLoaded', nv);
        }
    },
);

defineExpose<IDetailModalExposed>({
    show,
    hide,

    /**
     * Show an error
     * @param error
     * @returns
     */
    setError: (error: string | null) => {
        externalErrorMessage.value = error ? new FieldAlertMessage('', error, 'danger', error) : null;
    },
});
</script>

<template>
    <ModalComp
        ref="modaldialog"
        :size="detailSize ?? 'lg'"
        :title="getTitle()"
        :title-icon="detailComp?.changed ? 'fa-pencil' : ''"
        :title-icon-class="detailComp?.changed ? 'text-warning' : ''"
        :min-height="50"
        :open-centered="false"
        @closed="modalClosed"
        @visible="isVisible"
    >
        <template #exitButton>
            <ButtonComp
                button-type="Custom"
                class="btn btn-dark ms-auto"
                @click.capture="tryCloseDetail"
                ><IconComp icon="fa-xmark"></IconComp>
            </ButtonComp>
        </template>
        <template #content>
            <LoadingComp
                v-if="detailComp?.loadstate != null"
                :state="toValue(detailComp?.loadstate)"
                class="mt-5"
            />
            <div class="d-flex flex-column pt-3 h-100">
                <AlertMessage
                    type="danger"
                    :show-icon="false"
                    :dismissable="false"
                    class="flex-fill"
                    innerclass="flex-fill"
                    v-if="detailComp && detailcompErrorMessages.length > 0"
                >
                    <AlertMessage
                        type="danger"
                        size="xs"
                        v-for="message in detailcompErrorMessages"
                        :key="message.field"
                        :show-icon="false"
                        :dismissable="false"
                    >
                        <div v-html="$ti(message.alertMessage?.message ?? '', { field: message.field ?? ' ' }, true)"></div>
                    </AlertMessage>
                </AlertMessage>
                <AlertMessage
                    type="warning"
                    :show-icon="false"
                    :dismissable="false"
                    class="flex-fill"
                    innerclass="flex-fill"
                    v-if="detailComp && detailcompWarningMessages.length > 0"
                >
                    <AlertMessage
                        type="warning"
                        :show-icon="false"
                        :dismissable="false"
                        size="xs"
                        v-for="message in detailcompWarningMessages"
                        :key="message.field"
                    >
                        <div v-html="$ti(message.alertMessage?.message ?? '', { field: message.field ?? ' ' }, true)"></div>
                    </AlertMessage>
                </AlertMessage>

                <template v-if="detailView && id != null">
                    <component
                        :is="detailView"
                        :id="id"
                        ref="detailComp"
                        v-model="detailComp"
                    />
                </template>
            </div>
        </template>
        <template #footer>
            <ButtonGroup
                :actions="footerActions"
                v-if="footerActions?.length"
                :actionParam="id"
            />
            <div class="flex-grow-1"></div>
            <ButtonComp
                button-type="Close"
                @click.capture="tryCloseDetail"
                >{{ $tm('general.close') }}</ButtonComp
            >

            <ButtonComp
                :disabled="!canSave"
                button-type="Save"
                @click="saveDetail"
                :loadstate="saveState"
                >{{ $tm('button.save') }}</ButtonComp
            >
        </template>
    </ModalComp>
</template>
